import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/services/api";
import type { Application } from "./types";
import {
    bulkSetCachedApplicationDetails,
    type ApplicationDetailSnapshot,
} from "@/services/applicationDetailCache";

export interface Task {
    task_id: number;
    external_id: string | null;
    applicant_id: number;
    subject: string;
    status: string;
    type: string | null;
    record_type: string | null;
    is_message: boolean;
    application: string | null;
    application_name: string | null;
    application_number: string | null;
    message: string | null;
    public_url: string | null;
    sort_order: number | null;
    read: boolean;
    created_at: string | null;
    curriculum_vitae: string | null;
    document_id: number | null;
    document_status: string | null;
    document_name: string | null;
    document_public_url: string | null;
    document_type: string | null;
    document_maximum_size: number | null;
    document_description: string | null;
    document_message: string | null;
    document_required: boolean | null;
    questions: TaskQuestion[];
}

export interface TaskQuestion {
    id: string;
    db_id: number;
    question: string | null;
    answer: string | null;
    question_data_type: string | null;
    picklist_values: string | null;
    required: boolean;
    section_name: string | null;
    display_condition: string | null;
    parent_cv_record_type: string | null;
    description: string | null;
    sort_order: number | null;
    setting_record_id: string | null;
}

export interface ConversationMessage {
    id: number;
    external_id: string | null;
    subject: string;
    message: string | null;
    from_field: string | null;
    status: string | null;
    read: boolean;
    created_at: string | null;
}

export interface PortalConversation {
    id: number;
    application_external_id: string;
    application_name: string | null;
    application_number: string | null;
    subject: string;
    is_closed: boolean;
    updated_at: string | null;
    messages: ConversationMessage[];
}

interface Announce {
    id: string;
    header?: string;
    description?: string;
    image_url?: string;
    target_url?: string;
}

interface PendingProgramSelection {
    term: string;
    degree: string;
    /** Ordered list of program IDs (index 0 = first choice). */
    programs: string[];
    applicationType: string;
    timestamp: number;
}

interface PortalContextValue {
    applications: Application[];
    applicationsLoading: boolean;
    announces: Announce[];
    tasks: Task[];
    tasksLoading: boolean;
    conversations: PortalConversation[];
    conversationsLoading: boolean;
    announcesLoading: boolean;
    isApplying: boolean;
    resumeStep: number;
    resumeAppNumber: string | undefined;
    pendingProgramSelection: PendingProgramSelection | null;
    setIsApplying: (value: boolean) => void;
    startApplication: (appNumber?: string) => void;
    startApplicationWithSelection: (selection: PendingProgramSelection) => void;
    clearPendingSelection: () => void;
    handleComplete: (app: Application) => void;
    handleApplicationSubmit: (app: Application) => void;
    cancelApplying: () => void;
    refreshTasks: () => Promise<void>;
    refreshConversations: () => Promise<void>;
    markTaskRead: (taskId: number, shouldComplete?: boolean) => Promise<void>;
}

export const PortalContext = createContext<PortalContextValue | null>(null);
export type { PortalContextValue };

export function PortalProvider({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const [applications, setApplications] = useState<Application[]>([]);
    const [applicationsLoading, setApplicationsLoading] = useState(true);
    const [announces, setAnnounces] = useState<Announce[]>([]);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [tasksLoading, setTasksLoading] = useState(true);
    const [conversations, setConversations] = useState<PortalConversation[]>([]);
    const [conversationsLoading, setConversationsLoading] = useState(true);
    const [announcesLoading, setAnnouncesLoading] = useState(true);
    const [isApplying, setIsApplying] = useState(false);
    const [resumeStep, setResumeStep] = useState(0);
    const [resumeAppNumber, setResumeAppNumber] = useState<string | undefined>(undefined);
    const [pendingProgramSelection, setPendingProgramSelection] = useState<PendingProgramSelection | null>(null);

    // Check for pending program selection from localStorage on mount
    useEffect(() => {
        const stored = localStorage.getItem('pendingProgramSelection');
        if (stored) {
            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const raw = JSON.parse(stored) as any;
                // Backward-compat: old entries stored a single `program` string
                const programs: string[] = Array.isArray(raw.programs)
                    ? raw.programs.filter((p: unknown) => typeof p === 'string' && p.trim() !== '')
                    : raw.program
                        ? [String(raw.program)]
                        : [];

                const term = typeof raw.term === 'string' ? raw.term.trim() : '';
                const degree = typeof raw.degree === 'string' ? raw.degree.trim() : '';

                // Reject malformed entries: term, degree and at least one program are required
                if (!term || !degree || programs.length === 0) {
                    localStorage.removeItem('pendingProgramSelection');
                    return;
                }

                const selection: PendingProgramSelection = {
                    term,
                    degree,
                    programs,
                    applicationType: typeof raw.applicationType === 'string' && raw.applicationType.trim()
                        ? raw.applicationType.trim()
                        : "First Year Student",
                    timestamp: typeof raw.timestamp === 'number' ? raw.timestamp : Date.now(),
                };
                // Only use selection if it's less than 30 minutes old
                const thirtyMinutes = 30 * 60 * 1000;
                if (Date.now() - selection.timestamp < thirtyMinutes) {
                    setPendingProgramSelection(selection);
                } else {
                    localStorage.removeItem('pendingProgramSelection');
                }
            } catch {
                localStorage.removeItem('pendingProgramSelection');
            }
        }
    }, []);

    const refreshTasks = useCallback(async () => {
        setTasksLoading(true);
        try {
            const taskRes = await api.get('/integrations/hub/tasks/');
            if (taskRes.data.status === 'success') {
                setTasks((prev) => {
                    const readSet = new Set(
                        prev.filter((t) => t.read).map((t) => t.task_id)
                    );
                    return taskRes.data.tasks.map((t: Task) =>
                        readSet.has(t.task_id) ? { ...t, read: true } : t
                    );
                });
            }
        } catch (error) {
            console.error('Failed to fetch tasks:', error);
        } finally {
            setTasksLoading(false);
        }
    }, []);

    const markTaskRead = useCallback(async (taskId: number, shouldComplete = false) => {
        setTasks((prev) => prev.map((t) => t.task_id === taskId ? { ...t, read: true } : t));
        try {
            if (shouldComplete) {
                await api.post(`/integrations/hub/tasks/${taskId}/complete/`);
                setTasks((prev) => prev.map((t) => t.task_id === taskId ? { ...t, read: true, status: 'Completed' } : t));
                await refreshTasks();
            }
            await api.patch(`/integrations/hub/tasks/${taskId}/read/`);
        } catch (err) {
            console.error('markTaskRead failed:', err);
            setTasks((prev) => prev.map((t) => t.task_id === taskId ? { ...t, read: false } : t));
        }
    }, [refreshTasks]);

    const refreshConversations = useCallback(async () => {
        setConversationsLoading(true);
        try {
            const res = await api.get('/integrations/hub/messages/');
            if (res.data.status === 'success') {
                setConversations((prev) => {
                    const readMsgIds = new Set<number>();
                    prev.forEach((c) => c.messages.forEach((m) => { if (m.read) readMsgIds.add(m.id); }));
                    return (res.data.conversations as PortalConversation[]).map((c) => ({
                        ...c,
                        messages: c.messages.map((m) => readMsgIds.has(m.id) ? { ...m, read: true } : m),
                    }));
                });
            }
        } catch (error) {
            console.error('Failed to fetch conversations:', error);
        } finally {
            setConversationsLoading(false);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            try {
                const annRes = await api.get('/integrations/hub/announces/');
                if (annRes.data.status === 'success') {
                    setAnnounces(annRes.data.announces.map((a: Record<string, string>) => ({
                        id: a.id,
                        header: a.header,
                        description: a.description,
                        image_url: a.image_url,
                        target_url: a.target_url,
                    })));
                }
            } catch (error) {
                console.error('Failed to fetch announces:', error);
            } finally {
                setAnnouncesLoading(false);
            }

            try {
                const response = await api.get('/integrations/hub/applications/');
                const data = response.data;
                if (data.status === 'success') {
                    const mapped: Application[] = data.applications.map((a: Record<string, string>) => ({
                        Application_Number: a.application_id || '',
                        Application_External_ID: a.application_external_id || '',
                        Applicant_Name: `${a.applicant_first_name || ''} ${a.applicant_last_name || ''}`.trim(),
                        Applied_Program_Name: a.program_name || a.program_code || '',
                        Stage: a.stage || '',
                        Applied_Term: a.applied_term || '',
                        Application_Status: a.status || '',
                        Action: a.stage === 'Signed Up' ? 'Complete' : 'View',
                        Updated_At: a.updated_at || '',
                        Date: new Date(a.created_at || Date.now()),
                        offer_letter_url: a.offer_letter_url || null,
                        show_offer_letter_on_portal: (a.show_offer_letter_on_portal as unknown) === true || (a.show_offer_letter_on_portal as unknown) === 'true',
                        acceptance_letter_url: a.acceptance_letter_url || null,
                        show_acceptance_letter_on_portal: (a.show_acceptance_letter_on_portal as unknown) === true || (a.show_acceptance_letter_on_portal as unknown) === 'true',
                        summary: {
                            fullName: `${a.applicant_first_name || ''} ${a.applicant_last_name || ''}`.trim(),
                            email: a.member_email || '',
                            mobile: a.member_phone || '',
                            citizenship: a.member_citizenship || '',
                            appliedProgram: a.program_name || a.program_code || '',
                            appliedTerm: a.applied_term || '',
                            degree: a.degree_level || '',
                            stage: a.stage || '',
                        },
                    }));
                    setApplications(mapped);

                    // Pre-warm the detail cache immediately from the list data
                    // (DB values, no synced_at). This lets ApplicationView skip the
                    // loading spinner for 30 seconds even before the bulk SF sync below
                    // has completed.
                    bulkSetCachedApplicationDetails(
                        mapped.map((app) => ({
                            applicationNumber: app.Application_Number,
                            snapshot: {
                                applicationStatus: app.Application_Status || '',
                                stage: app.Stage || '',
                                offerLetterUrl: app.offer_letter_url ?? null,
                                showOfferLetterOnPortal: app.show_offer_letter_on_portal ?? false,
                                acceptanceLetterUrl: app.acceptance_letter_url ?? null,
                                showAcceptanceLetterOnPortal: app.show_acceptance_letter_on_portal ?? false,
                                syncedAt: null,
                            } satisfies ApplicationDetailSnapshot,
                        }))
                    );

                    // Await a bulk Salesforce sync before marking applications as
                    // loaded. This ensures the list is never shown with stale DB
                    // values — applicationsLoading stays true until fresh SF data
                    // is available. Failure is caught and degraded gracefully so
                    // the DB data still renders.
                    await api.get('/integrations/hub/applications/bulk-detail/')
                        .then((bulkRes) => {
                            if (bulkRes.data.status !== 'success') return;

                            const freshDetails: Array<{
                                application_number: string;
                                application_status: string;
                                stage: string;
                                offer_letter_url: string | null;
                                show_offer_letter_on_portal: boolean | null;
                                acceptance_letter_url: string | null;
                                show_acceptance_letter_on_portal: boolean | null;
                                synced_at: string | null;
                            }> = bulkRes.data.applications ?? [];

                            // Overwrite the pre-warm with authoritative SF-synced values.
                            bulkSetCachedApplicationDetails(
                                freshDetails.map((detail) => ({
                                    applicationNumber: detail.application_number,
                                    snapshot: {
                                        applicationStatus: detail.application_status || '',
                                        stage: detail.stage || '',
                                        offerLetterUrl: detail.offer_letter_url ?? null,
                                        showOfferLetterOnPortal: detail.show_offer_letter_on_portal ?? false,
                                        acceptanceLetterUrl: detail.acceptance_letter_url ?? null,
                                        showAcceptanceLetterOnPortal: detail.show_acceptance_letter_on_portal ?? false,
                                        syncedAt: detail.synced_at ?? null,
                                    } satisfies ApplicationDetailSnapshot,
                                }))
                            );

                            // Refresh the applications list state so the list
                            // view reflects any stage/status changes from Salesforce.
                            setApplications((prev) => {
                                const freshByNumber = new Map(
                                    freshDetails.map((detail) => [detail.application_number, detail] as const)
                                );

                                return prev.map((app) => {
                                    const fresh = freshByNumber.get(app.Application_Number);
                                    if (!fresh) return app;
                                    return {
                                        ...app,
                                        Stage: fresh.stage || app.Stage,
                                        Application_Status: fresh.application_status || app.Application_Status,
                                        offer_letter_url: fresh.offer_letter_url ?? app.offer_letter_url,
                                        show_offer_letter_on_portal: fresh.show_offer_letter_on_portal ?? app.show_offer_letter_on_portal,
                                        acceptance_letter_url: fresh.acceptance_letter_url ?? app.acceptance_letter_url,
                                        show_acceptance_letter_on_portal: fresh.show_acceptance_letter_on_portal ?? app.show_acceptance_letter_on_portal,
                                    };
                                });
                            });
                        })
                        .catch((bulkErr) => {
                            // Non-fatal — DB data already in state; individual
                            // ApplicationView fetches serve as fallback.
                            console.warn('Bulk application detail fetch failed:', bulkErr);
                        });
                }
            } catch (error) {
                console.error('Failed to fetch applications:', error);
            } finally {
                setApplicationsLoading(false);
            }

            await refreshTasks();
            await refreshConversations();
        };

        init();
    }, [refreshTasks, refreshConversations]);

    const startApplication = useCallback((appNumber?: string) => {
        setResumeAppNumber(appNumber);
        setResumeStep(0);
        setIsApplying(true);
        navigate('/portal/create-application' + (appNumber ? `?id=${appNumber}` : ''));
    }, [navigate]);

    const startApplicationWithSelection = useCallback((selection: PendingProgramSelection) => {
        // Clear the pending selection from localStorage
        localStorage.removeItem('pendingProgramSelection');
        setPendingProgramSelection(null);

        // Navigate to create application — pass ordered program IDs as comma-separated param
        const params = new URLSearchParams({
            term: selection.term,
            degree: selection.degree,
            programs: selection.programs.join(','),
            applicationType: selection.applicationType,
        });
        setResumeAppNumber(undefined);
        setResumeStep(0);
        setIsApplying(true);
        navigate(`/portal/create-application?${params}`);
    }, [navigate]);

    const clearPendingSelection = useCallback(() => {
        localStorage.removeItem('pendingProgramSelection');
        setPendingProgramSelection(null);
    }, []);

    const handleComplete = useCallback((app: Application) => {
        setResumeStep(app.currentStep ?? 0);
        setResumeAppNumber(app.Application_Number);
        setIsApplying(true);
        navigate(`/portal/create-application?id=${app.Application_Number}`);
    }, [navigate]);

    const cancelApplying = useCallback(() => {
        setIsApplying(false);
        setResumeStep(0);
        setResumeAppNumber(undefined);
    }, []);

    const handleApplicationSubmit = useCallback((app: Application) => {
        setApplications((prev) => {
            const key = resumeAppNumber ?? app.Application_Number;
            const exists = prev.some((a) => a.Application_Number === key);
            if (exists) {
                return prev.map((a) => (a.Application_Number === key ? app : a));
            }
            return [app, ...prev];
        });
        setIsApplying(false);
        setResumeStep(0);
        setResumeAppNumber(undefined);
        navigate(`/portal/applications/${app.Application_Number}`, { state: { newApplication: true } });
    }, [navigate, resumeAppNumber]);

    return (
        <PortalContext.Provider value={{
            applications,
            applicationsLoading,
            announces,
            tasks,
            tasksLoading,
            conversations,
            conversationsLoading,
            announcesLoading,
            isApplying,
            resumeStep,
            resumeAppNumber,
            pendingProgramSelection,
            setIsApplying,
            startApplication,
            startApplicationWithSelection,
            clearPendingSelection,
            handleComplete,
            handleApplicationSubmit,
            cancelApplying,
            refreshTasks,
            refreshConversations,
            markTaskRead,
        }}>
            {children}
        </PortalContext.Provider>
    );
}

export function usePortal() {
    const context = useContext(PortalContext);
    if (!context) throw new Error('usePortal must be used within PortalProvider');
    return context;
}
